/**
 * Utility functions for handling file downloads across different platforms (desktop, mobile, PWA)
 */

/**
 * Detects if the current environment is mobile or PWA
 */
export const isMobileOrPWA = (): boolean => {
  // Check if running as PWA (standalone mode)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || (window.navigator as any).standalone 
    || document.referrer.includes('android-app://');
  
  // Check if mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  
  // Check screen size as additional indicator
  const isSmallScreen = window.innerWidth < 768;
  
  return isStandalone || isMobile || isSmallScreen;
};

/**
 * Downloads a file from a URL, automatically handling mobile/PWA vs desktop scenarios
 * @param url - The URL of the file to download
 * @param fileName - The name to save the file as
 * @param onSuccess - Optional callback when download succeeds
 * @param onError - Optional callback when download fails
 */
export const downloadFileFromUrl = async (
  url: string,
  fileName: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void
): Promise<void> => {
  if (!url) {
    const error = new Error('No URL provided for download');
    console.error(error);
    onError?.(error);
    return;
  }

  const isMobileOrPwa = isMobileOrPWA();

  if (isMobileOrPwa) {
    // Mobile/PWA: Download as blob for better compatibility
    try {
      // Fetch the file as a blob
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Create blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
      
      onSuccess?.();
    } catch (error) {
      console.error('Failed to download file:', error);
      
      // Fallback: try opening in new tab
      try {
        window.open(url, '_blank', 'noopener,noreferrer');
        onSuccess?.();
      } catch (fallbackError) {
        const err = error instanceof Error ? error : new Error('Unknown download error');
        onError?.(err);
      }
    }
  } else {
    // Desktop: Open in new tab (existing behavior)
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
      onSuccess?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to open file');
      console.error('Failed to open file:', err);
      onError?.(err);
    }
  }
};

/**
 * Triggers a direct download of a file using an anchor element
 * Useful when you already have the URL and want to force download
 */
export const triggerDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
